import * as utils from '@lib/utils';
import React, { useEffect } from 'react';
import {
  Text as RNText,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View
} from 'react-native';
import Animated, {
  Easing,
  useAnimatedProps,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Defs, Line, LinearGradient, Path, Stop } from 'react-native-svg';


import Down from '@assets/icons/down-dark';

import { Modal, Row, Spacer, Text } from "@components/1-atoms";

import { ChartDataPointProps } from '@/interfaces/chart.interface';
import ListItem from '../../ListItem';
import importedStyles from './../styles';


const AnimatedPath = Animated.createAnimatedComponent(Path);

interface GiftedAreaChartProps {
  data: ChartDataPointProps[];
  height?: number;
  spacing?: number;
  initialSpacing?: number;
  endSpacing?: number;
  curved?: boolean; // Toggle straight line vs smooth curve
  lineColor?: string;
  lineWidth?: number;

  // Strip lines
  showStrip?: boolean;
  stripHeight?: number;
  stripWidth?: number;
  stripColor?: string;

  // Grid Lines
  noOfSections?: number;
  rulesColor?: string;
  rulesType?: 'solid' | 'dashed';
  // Interactions & Animation
  animationDuration?: number;
  // onPressLabel?: (point: DataPoint, index: number) => void;
}

export const GiftedAreaChart: React.FC<GiftedAreaChartProps> = ({
  data,
  height = 200,
  spacing = 70,
  initialSpacing = 40,
  endSpacing = 40,
  curved = true,
  lineColor = '#2563eb', // Rich Solid Royal Blue
  lineWidth = 3, // Thick line accent on top

  showStrip = true,
  stripHeight,
  stripWidth = 2,
  stripColor = '#dbeafe',

  noOfSections = 3,
  rulesColor = '#e2e8f0',
  rulesType = 'dashed',

  animationDuration = 1200 * 2,
  // onPressLabel,
}) => {
  const scrollViewRef = React.useRef<null | ScrollView>(null);

  type BalanceType = { key: "debitBalance" | "creditBalance", name: string };
  const balanceTypes: Array<BalanceType> = [
    { key: 'debitBalance', name: 'Available' },
    { key: 'creditBalance', name: 'Outstanding' },
  ];
  const [selectedBalanceType, setSelectedBalanceType] = React.useState<BalanceType>(balanceTypes[0]);
  const [selectedPointDataIndex, setSelectedPointDataIndex] = React.useState<number | null>(null);


  React.useEffect(() => {
    const defaultIndex = data.length ? data.length - 1 : null;
    setSelectedPointDataIndex(defaultIndex);
  }, [data]);

  const onPressLabel = (point: ChartDataPointProps, index: number) => {
    setSelectedPointDataIndex(index);
  };

  const selectedPoint = (selectedPointDataIndex != null) ? data[selectedPointDataIndex] : null;
  const selectedPointBalance = (selectedPoint != null) ? utils.printDecimalOf(selectedPoint[selectedBalanceType.key]) : "---";
  const currentBalanceDate = (selectedPoint != null) ? utils.formatDate(selectedPoint.date) : "---";

  // Balanncne type toggler modal
  const [isBalanceTypeModalVisible, setBalanceTypeModalVisiblility] = React.useState(false);
  const openBalanceTypeModal = () => setBalanceTypeModalVisiblility(true);
  const closeBalanceTypeModal = () => setBalanceTypeModalVisiblility(false);

  // Update the account chart data to display
  const updateBalanceType = (type: any) => {
    closeBalanceTypeModal();
    setSelectedBalanceType(type);
  };

  React.useEffect(() => {
    if (scrollViewRef?.current) {
      scrollViewRef.current?.scrollToEnd({ animated: false });
    }
  }, [scrollViewRef /* , scrollViewRef?.current */]);

  const paddingBottom = 35;
  const chartHeight = height - paddingBottom;
  const totalContentWidth =
    initialSpacing + endSpacing + (data.length - 1) * spacing;

  const maxVal = Math.max(...data.map((d) => d[selectedBalanceType.key]), 1) * 1.1;
  const minVal = 0; // Standard layout baseline tracking
  const valRange = maxVal - minVal;

  // Shared layout growth driver
  const animationProgress = useSharedValue(0);

  useEffect(() => {
    animationProgress.value = withTiming(1, {
      duration: animationDuration,
      easing: Easing.out(Easing.cubic),
    });
  }, [data, animationProgress, animationDuration]);

  // Compute standard base tracking nodes
  const points = data.map((d, i) => ({
    x: initialSpacing + i * spacing,
    y: chartHeight - ((d[selectedBalanceType.key] - minVal) / valRange) * (chartHeight - 20),
  }));

  // Reanimated path generator injection matrix
  const animatedProps = useAnimatedProps(() => {
    if (points.length === 0) return { d: '' };

    // Scale Y positions dynamically relative to interpolation timelines
    const scaledPoints = points.map((p) => {
      const distanceToBaseline = chartHeight - p.y;
      return {
        x: p.x,
        y: chartHeight - distanceToBaseline * animationProgress.value,
      };
    });

    let linePath = `M ${scaledPoints[0].x} ${scaledPoints[0].y}`;

    if (curved) {
      for (let i = 0; i < scaledPoints.length - 1; i++) {
        const p0 = scaledPoints[i];
        const p1 = scaledPoints[i + 1];
        const cpX1 = p0.x + (p1.x - p0.x) / 2;
        const cpY1 = p0.y;
        const cpX2 = p0.x + (p1.x - p0.x) / 2;
        const cpY2 = p1.y;
        linePath += ` C ${cpX1} ${cpY1}, ${cpX2} ${cpY2}, ${p1.x} ${p1.y}`;
      }
    } else {
      for (let i = 1; i < scaledPoints.length; i++) {
        linePath += ` L ${scaledPoints[i].x} ${scaledPoints[i].y}`;
      }
    }

    const fillPath = `${linePath} L ${scaledPoints[scaledPoints.length - 1].x
      } ${chartHeight} L ${scaledPoints[0].x} ${chartHeight} Z`;
    return { d: fillPath };
  });

  const animatedLineProps = useAnimatedProps(() => {
    if (points.length === 0) return { d: '' };

    const scaledPoints = points.map((p) => {
      const distanceToBaseline = chartHeight - p.y;
      return {
        x: p.x,
        y: chartHeight - distanceToBaseline * animationProgress.value,
      };
    });

    let linePath = `M ${scaledPoints[0].x} ${scaledPoints[0].y}`;
    if (curved) {
      for (let i = 0; i < scaledPoints.length - 1; i++) {
        const p0 = scaledPoints[i];
        const p1 = scaledPoints[i + 1];
        const cpX1 = p0.x + (p1.x - p0.x) / 2;
        const cpY1 = p0.y;
        const cpX2 = p0.x + (p1.x - p0.x) / 2;
        const cpY2 = p1.y;
        linePath += ` C ${cpX1} ${cpY1}, ${cpX2} ${cpY2}, ${p1.x} ${p1.y}`;
      }
    } else {
      for (let i = 1; i < scaledPoints.length; i++) {
        linePath += ` L ${scaledPoints[i].x} ${scaledPoints[i].y}`;
      }
    }
    return { d: linePath };
  });

  // Background Grid Line Sections Array Calculation
  const sections = Array.from({ length: noOfSections + 1 });

  return (
    <View style={importedStyles.viewport}>

      <View style={importedStyles.headerRow}>
        <View>
          <RNText style={importedStyles.headerTitle}>
            $ {selectedPointBalance}
          </RNText>
          <Spacer h={.6} />

          <TouchableOpacity onPress={openBalanceTypeModal} >
            <Row alignment="center">
              <Text small>
                {`${selectedBalanceType.name} as of ${currentBalanceDate} `}
              </Text>
              <Down />
            </Row>
          </TouchableOpacity>

        </View>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        ref={scrollViewRef}>
        <View style={{ width: totalContentWidth, height }}>
          {/* The line chart in SVG */}
          <Svg
            width={totalContentWidth}
            height={chartHeight}
            style={StyleSheet.absoluteFill}>
            {/* Grid Rules Background Rendering */}
            {sections.map((_, i) => {
              const yPos = 20 + (i / noOfSections) * (chartHeight - 40);
              return (
                <Line
                  key={i}
                  x1={0}
                  y1={yPos}
                  x2={totalContentWidth}
                  y2={yPos}
                  stroke={rulesColor}
                  strokeWidth={1}
                  strokeDasharray={rulesType === 'dashed' ? '5,5' : undefined}
                />
              );
            })}

            {/* Optional Component Vertical Layout Tracker Strips */}
            {showStrip &&
              points.map((pt, idx) => (
                <Line
                  key={`strip-${idx}`}
                  x1={pt.x}
                  y1={chartHeight - (stripHeight ?? chartHeight - pt.y)}
                  x2={pt.x}
                  y2={chartHeight}
                  stroke={stripColor}
                  strokeWidth={stripWidth}
                />
              ))}


            {/* Vibrant Radiant blue gradient setup matching screenshot layout exactly */}
            <Defs>
              <LinearGradient id="radiantBlueGrad" x1="0" y1="0" x2="0" y2="1">
                <Stop
                  offset="0%"
                  stopColor="rgb(46, 217, 255)"
                  stopOpacity="0.8"
                />
                <Stop
                  offset="100%"
                  stopColor="rgb(203, 241, 250)"
                  stopOpacity="0.0"
                />
              </LinearGradient>
            </Defs>
            {/* Area Fill Layer */}
            <AnimatedPath animatedProps={animatedProps} fill="url(#radiantBlueGrad)" />

            {/* Solid High-Density Stroke Accent Layer on Top */}
            <AnimatedPath
              animatedProps={animatedLineProps}
              fill="none"
              stroke={lineColor}
              strokeWidth={lineWidth}
            />
          </Svg>

          {/* Interactive Navigation Control Footers */}
          <View
            style={[
              importedStyles.xAxis,
              /* 11px as margin bottom (or distance away from the chart - relative position) */
              { width: totalContentWidth, top: (chartHeight + 11) },
            ]}>
            {points.map((pt, index) => (
              <TouchableOpacity
                key={index}
                activeOpacity={0.7}
                style={[
                  importedStyles.labelTouchable,
                  (index === selectedPointDataIndex) ? importedStyles.touchableLabelSelected : {},
                  { left: pt.x - 42 / 2 },
                ]}
                onPress={() => onPressLabel?.(data[index], index)}>
                <RNText style={importedStyles.labelText}>{data[index].label}</RNText>
              </TouchableOpacity>
            ))}
          </View>

        </View>
      </ScrollView>

      {/* A modal to toggle between available and outstanding balance */}
      <Modal visible={isBalanceTypeModalVisible} title={"Balance type"} onClose={closeBalanceTypeModal}>

        {
          balanceTypes.map((item) => {
            return (
              <ListItem key={item.key} onPress={() => { updateBalanceType(item) }} >
                <Text bold={selectedBalanceType.key === item.key ? true : false}>{item.name}</Text>
              </ListItem>
            );
          })
        }

      </Modal>
    </View>
  );
};


export default GiftedAreaChart;